import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Edit3,
  EyeOff,
  Eye,
  X,
  GripVertical,
  ArrowUp,
  ArrowDown,
  Sparkles,
  Lock,
  Plus,
  AlignLeft,
  Calendar,
  Camera,
  CheckSquare,
  Hash,
  ListChecks,
  Type as TypeIcon,
  User as BodyIcon,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Header } from '@/components/layout/Header';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';

/* ==========================================================================
   S11 — Admin Editor Domande

   Lista domande per scheda/sezione, riordino, toggle visibilità,
   editor inline con preview.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types & constants
   -------------------------------------------------------------------------- */

type SessionType = 'welcome' | 'beauty' | 'kinesiology' | 'medical';

type FieldType =
  | 'text'
  | 'textarea'
  | 'select'
  | 'multi_select'
  | 'number'
  | 'boolean'
  | 'date'
  | 'photos'
  | 'body_map';

interface FormField {
  id: number;
  sessionType: SessionType;
  section: string;
  fieldKey: string;
  label: string;
  fieldType: FieldType;
  options: string[] | null;
  validationRules: Record<string, unknown> | null;
  isRequired: boolean;
  isSystem: boolean;
  isVisible: boolean;
  isPolishable: boolean;
  sortOrder: number;
}

const SESSION_TABS: Array<{ value: SessionType; label: string }> = [
  { value: 'welcome', label: 'Welcome' },
  { value: 'beauty', label: 'Beauty' },
  { value: 'kinesiology', label: 'Kinesiologia' },
  { value: 'medical', label: 'Medico' },
];

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: 'Testo breve',
  textarea: 'Testo lungo',
  select: 'Selezione',
  multi_select: 'Selezione multipla',
  number: 'Numero',
  boolean: 'Sì / No',
  date: 'Data',
  photos: 'Foto',
  body_map: 'Body map',
};

const FIELD_TYPE_ICONS: Record<FieldType, typeof TypeIcon> = {
  text: TypeIcon,
  textarea: AlignLeft,
  select: ListChecks,
  multi_select: ListChecks,
  number: Hash,
  boolean: CheckSquare,
  date: Calendar,
  photos: Camera,
  body_map: BodyIcon,
};

const FIELD_TYPES_WITH_OPTIONS: FieldType[] = ['select', 'multi_select'];

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function AdminQuestions() {
  const toast = useToast();

  const [sessionType, setSessionType] = useState<SessionType>('beauty');
  const [fields, setFields] = useState<FormField[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingField, setEditingField] = useState<FormField | null>(null);

  // Drag state
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);

  // ------------------------------------------------------------------
  // Fetch fields (include hidden too — admin needs to see all)
  // ------------------------------------------------------------------

  const loadFields = useCallback(async (type: SessionType) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<FormField[]>(
        `/form-fields?sessionType=${type}`,
      );
      setFields(data ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Errore di connessione');
      setFields([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFields(sessionType);
  }, [loadFields, sessionType]);

  // ------------------------------------------------------------------
  // Group by section, preserving original order
  // ------------------------------------------------------------------

  const grouped = useMemo(() => {
    const map = new Map<string, FormField[]>();
    for (const f of fields) {
      const arr = map.get(f.section) ?? [];
      arr.push(f);
      map.set(f.section, arr);
    }
    return Array.from(map.entries()).map(([section, items]) => ({
      section,
      items: items.slice().sort((a, b) => a.sortOrder - b.sortOrder),
    }));
  }, [fields]);

  // ------------------------------------------------------------------
  // Toggle visibility
  // ------------------------------------------------------------------

  const handleToggleVisible = useCallback(
    async (field: FormField) => {
      const next = !field.isVisible;
      setFields((prev) =>
        prev.map((f) => (f.id === field.id ? { ...f, isVisible: next } : f)),
      );
      try {
        await api.put(`/form-fields/${field.id}`, { isVisible: next });
        toast.success(next ? 'Domanda visibile' : 'Domanda nascosta');
      } catch (err) {
        // Rollback
        setFields((prev) =>
          prev.map((f) =>
            f.id === field.id ? { ...f, isVisible: !next } : f,
          ),
        );
        toast.error(err instanceof ApiError ? err.message : 'Aggiornamento fallito');
      }
    },
    [toast],
  );

  // ------------------------------------------------------------------
  // Reorder within a section
  // ------------------------------------------------------------------

  const persistReorder = useCallback(
    async (section: string, orderedIds: number[]) => {
      try {
        await api.patch('/form-fields/reorder', {
          sessionType,
          section,
          fieldIds: orderedIds,
        });
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'Riordino fallito');
        // Reload to restore truth
        void loadFields(sessionType);
      }
    },
    [sessionType, loadFields, toast],
  );

  const moveField = useCallback(
    (field: FormField, direction: -1 | 1) => {
      const sectionItems = grouped.find((g) => g.section === field.section)?.items ?? [];
      const idx = sectionItems.findIndex((f) => f.id === field.id);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= sectionItems.length) return;

      const reordered = sectionItems.slice();
      const [moved] = reordered.splice(idx, 1);
      reordered.splice(target, 0, moved);

      // Optimistic update of sortOrder
      setFields((prev) =>
        prev.map((f) => {
          const newIdx = reordered.findIndex((r) => r.id === f.id);
          if (newIdx < 0 || f.section !== field.section) return f;
          return { ...f, sortOrder: newIdx };
        }),
      );

      void persistReorder(
        field.section,
        reordered.map((f) => f.id),
      );
    },
    [grouped, persistReorder],
  );

  const handleDrop = useCallback(
    (section: string, draggedFieldId: number, targetFieldId: number) => {
      if (draggedFieldId === targetFieldId) return;

      const sectionItems = grouped.find((g) => g.section === section)?.items ?? [];
      const draggedField = sectionItems.find((f) => f.id === draggedFieldId);
      const targetField = sectionItems.find((f) => f.id === targetFieldId);
      if (!draggedField || !targetField) return;

      const without = sectionItems.filter((f) => f.id !== draggedFieldId);
      const targetIdx = without.findIndex((f) => f.id === targetFieldId);
      const reordered = [
        ...without.slice(0, targetIdx),
        draggedField,
        ...without.slice(targetIdx),
      ];

      setFields((prev) =>
        prev.map((f) => {
          const newIdx = reordered.findIndex((r) => r.id === f.id);
          if (newIdx < 0 || f.section !== section) return f;
          return { ...f, sortOrder: newIdx };
        }),
      );

      void persistReorder(
        section,
        reordered.map((f) => f.id),
      );
    },
    [grouped, persistReorder],
  );

  // ------------------------------------------------------------------
  // Save edited field
  // ------------------------------------------------------------------

  const handleSaveField = useCallback(
    async (id: number, patch: Partial<FormField>) => {
      try {
        const updated = await api.put<FormField>(`/form-fields/${id}`, patch);
        if (updated) {
          setFields((prev) =>
            prev.map((f) => (f.id === id ? { ...f, ...updated } : f)),
          );
        }
        toast.success('Domanda aggiornata');
        setEditingField(null);
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'Salvataggio fallito');
      }
    },
    [toast],
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <AppShell header={<Header title="Editor Domande" showBack />}>
      {/* Tab bar — sessionType */}
      <div className="flex gap-2 mb-6 overflow-x-auto -mx-5 px-5 scrollbar-hide">
        {SESSION_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setSessionType(tab.value)}
            className={cn(
              'shrink-0 px-4 py-2 rounded-[var(--radius-md)] text-sm font-medium',
              'transition-colors duration-[var(--duration-fast)]',
              sessionType === tab.value
                ? 'bg-accent-primary text-white'
                : 'bg-surface-sunken text-text-secondary hover:bg-border-default',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={80} className="rounded-[var(--radius-lg)]" />
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center py-12 text-center">
          <p className="text-sm text-text-muted mb-4">{error}</p>
          <Button variant="secondary" size="sm" onClick={() => void loadFields(sessionType)}>
            Riprova
          </Button>
        </div>
      ) : grouped.length === 0 ? (
        <div className="flex flex-col items-center py-12 text-center">
          <Plus className="h-12 w-12 text-text-muted/40 mb-4" strokeWidth={1} />
          <p className="text-sm text-text-muted">
            Nessuna domanda configurata per questo tipo di sessione.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(({ section, items }) => (
            <section key={section}>
              <h2 className="font-[family-name:var(--font-heading)] text-base font-bold text-text-primary mb-3 uppercase tracking-wide">
                {section}
              </h2>
              <div className="space-y-2">
                {items.map((field, idx) => (
                  <FieldRow
                    key={field.id}
                    field={field}
                    canMoveUp={idx > 0}
                    canMoveDown={idx < items.length - 1}
                    isDragOver={dropTargetId === field.id && draggedId !== field.id}
                    onMoveUp={() => moveField(field, -1)}
                    onMoveDown={() => moveField(field, 1)}
                    onToggleVisible={() => void handleToggleVisible(field)}
                    onEdit={() => setEditingField(field)}
                    onDragStart={() => setDraggedId(field.id)}
                    onDragEnd={() => {
                      setDraggedId(null);
                      setDropTargetId(null);
                    }}
                    onDragOver={() => {
                      if (draggedId !== null && draggedId !== field.id) {
                        setDropTargetId(field.id);
                      }
                    }}
                    onDrop={() => {
                      if (draggedId !== null) {
                        handleDrop(section, draggedId, field.id);
                      }
                      setDraggedId(null);
                      setDropTargetId(null);
                    }}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editingField && (
        <EditFieldModal
          field={editingField}
          onClose={() => setEditingField(null)}
          onSave={handleSaveField}
        />
      )}

      {/* Bottom spacer */}
      <div className="h-8" />
    </AppShell>
  );
}

/* --------------------------------------------------------------------------
   FieldRow
   -------------------------------------------------------------------------- */

interface FieldRowProps {
  field: FormField;
  canMoveUp: boolean;
  canMoveDown: boolean;
  isDragOver: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onToggleVisible: () => void;
  onEdit: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: () => void;
  onDrop: () => void;
}

function FieldRow({
  field,
  canMoveUp,
  canMoveDown,
  isDragOver,
  onMoveUp,
  onMoveDown,
  onToggleVisible,
  onEdit,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: FieldRowProps) {
  const Icon = FIELD_TYPE_ICONS[field.fieldType] ?? TypeIcon;

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
      className={cn(
        'flex items-center gap-3 p-3 rounded-[var(--radius-lg)] border bg-surface-card',
        'transition-all duration-[var(--duration-fast)]',
        isDragOver
          ? 'border-accent-primary ring-2 ring-accent-primary/20'
          : 'border-border-default',
        !field.isVisible && 'opacity-60',
      )}
    >
      {/* Drag handle */}
      <span
        className="text-text-muted cursor-grab active:cursor-grabbing shrink-0"
        title="Trascina per riordinare"
      >
        <GripVertical className="h-5 w-5" strokeWidth={1.5} />
      </span>

      {/* Type icon */}
      <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] bg-accent-secondary-light">
        <Icon className="h-4 w-4 text-accent-secondary" strokeWidth={2} />
      </span>

      {/* Label + meta */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-text-primary truncate">
          {field.label}
        </p>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <span className="text-xs text-text-muted">
            {FIELD_TYPE_LABELS[field.fieldType]}
          </span>
          {field.isRequired && (
            <Badge status="negative" size="sm">Obbligatorio</Badge>
          )}
          {field.isPolishable && (
            <Badge status="info" size="sm">
              <Sparkles className="h-3 w-3 mr-0.5 inline" strokeWidth={2} />
              AI
            </Badge>
          )}
          {field.isSystem && (
            <Badge status="neutral" size="sm">
              <Lock className="h-3 w-3 mr-0.5 inline" strokeWidth={2} />
              Sistema
            </Badge>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={onMoveUp}
          disabled={!canMoveUp}
          className="h-8 w-8 flex items-center justify-center rounded-[var(--radius-md)] text-text-muted hover:bg-surface-sunken disabled:opacity-30 disabled:pointer-events-none"
          aria-label="Sposta su"
        >
          <ArrowUp className="h-4 w-4" strokeWidth={2} />
        </button>
        <button
          onClick={onMoveDown}
          disabled={!canMoveDown}
          className="h-8 w-8 flex items-center justify-center rounded-[var(--radius-md)] text-text-muted hover:bg-surface-sunken disabled:opacity-30 disabled:pointer-events-none"
          aria-label="Sposta giù"
        >
          <ArrowDown className="h-4 w-4" strokeWidth={2} />
        </button>
        <button
          onClick={onToggleVisible}
          className="h-8 w-8 flex items-center justify-center rounded-[var(--radius-md)] text-text-muted hover:bg-surface-sunken"
          aria-label={field.isVisible ? 'Nascondi' : 'Mostra'}
        >
          {field.isVisible ? (
            <Eye className="h-4 w-4" strokeWidth={2} />
          ) : (
            <EyeOff className="h-4 w-4" strokeWidth={2} />
          )}
        </button>
        <button
          onClick={onEdit}
          className="h-8 w-8 flex items-center justify-center rounded-[var(--radius-md)] text-accent-primary hover:bg-accent-primary-light"
          aria-label="Modifica"
        >
          <Edit3 className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   EditFieldModal
   -------------------------------------------------------------------------- */

interface EditFieldModalProps {
  field: FormField;
  onClose: () => void;
  onSave: (id: number, patch: Partial<FormField>) => Promise<void>;
}

function EditFieldModal({ field, onClose, onSave }: EditFieldModalProps) {
  const [label, setLabel] = useState(field.label);
  const [fieldType, setFieldType] = useState<FieldType>(field.fieldType);
  const [isRequired, setIsRequired] = useState(field.isRequired);
  const [isPolishable, setIsPolishable] = useState(field.isPolishable);
  const [optionsText, setOptionsText] = useState(
    (field.options ?? []).join('\n'),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [labelError, setLabelError] = useState<string | null>(null);

  const showOptions = FIELD_TYPES_WITH_OPTIONS.includes(fieldType);
  const canChangeType = !field.isSystem;

  const handleSubmit = async () => {
    if (!label.trim()) {
      setLabelError('La label è obbligatoria');
      return;
    }
    setLabelError(null);
    setIsSaving(true);

    const patch: Partial<FormField> = {
      label: label.trim(),
      isRequired,
      isPolishable,
    };

    if (canChangeType && fieldType !== field.fieldType) {
      patch.fieldType = fieldType;
    }

    if (showOptions) {
      patch.options = optionsText
        .split('\n')
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
    }

    try {
      await onSave(field.id, patch);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-surface-card rounded-t-[var(--radius-xl)] sm:rounded-[var(--radius-xl)] shadow-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-center justify-between p-5 border-b border-border-default bg-surface-card">
          <div>
            <h2 className="font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary">
              Modifica domanda
            </h2>
            <p className="text-xs text-text-muted mt-0.5 font-mono">
              {field.fieldKey}
            </p>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 flex items-center justify-center rounded-[var(--radius-md)] text-text-muted hover:bg-surface-sunken"
            aria-label="Chiudi"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              Label
            </label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              error={labelError ?? undefined}
              placeholder="Es. Tipo di pelle"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              Tipo di campo
              {!canChangeType && (
                <span className="ml-2 text-xs text-text-muted font-normal">
                  (campo di sistema, non modificabile)
                </span>
              )}
            </label>
            <select
              value={fieldType}
              onChange={(e) => setFieldType(e.target.value as FieldType)}
              disabled={!canChangeType}
              className={cn(
                'w-full px-3 py-2 rounded-[var(--radius-md)] border border-border-default bg-surface-card',
                'text-sm text-text-primary',
                'focus:outline-none focus:ring-2 focus:ring-accent-primary/30',
                !canChangeType && 'opacity-60 cursor-not-allowed',
              )}
            >
              {(Object.keys(FIELD_TYPE_LABELS) as FieldType[]).map((t) => (
                <option key={t} value={t}>
                  {FIELD_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          {/* Options (only for select / multi_select) */}
          {showOptions && (
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">
                Opzioni
                <span className="ml-2 text-xs text-text-muted font-normal">
                  (una per riga)
                </span>
              </label>
              <textarea
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 rounded-[var(--radius-md)] border border-border-default bg-surface-card text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/30 font-mono"
                placeholder={'Secca\nGrassa\nMista\nSensibile'}
              />
            </div>
          )}

          {/* Toggles */}
          <div className="space-y-3">
            <ToggleRow
              checked={isRequired}
              onChange={setIsRequired}
              label="Obbligatoria"
              description="L'operatrice deve compilare questo campo per chiudere il check-up"
            />
            <ToggleRow
              checked={isPolishable}
              onChange={setIsPolishable}
              label="Polishing AI"
              description="Il testo verrà rivisto/migliorato automaticamente prima del Pride"
              icon={<Sparkles className="h-4 w-4" strokeWidth={2} />}
            />
          </div>

          {/* Preview */}
          <FieldPreview
            label={label || field.label}
            fieldType={fieldType}
            isRequired={isRequired}
            options={
              showOptions
                ? optionsText.split('\n').map((v) => v.trim()).filter(Boolean)
                : undefined
            }
          />
        </div>

        <div className="sticky bottom-0 flex gap-2 p-5 border-t border-border-default bg-surface-card">
          <Button variant="secondary" size="md" onClick={onClose} disabled={isSaving} className="flex-1">
            Annulla
          </Button>
          <Button variant="primary" size="md" onClick={handleSubmit} loading={isSaving} className="flex-1">
            Salva
          </Button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   ToggleRow
   -------------------------------------------------------------------------- */

interface ToggleRowProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

function ToggleRow({ checked, onChange, label, description, icon }: ToggleRowProps) {
  return (
    <label className="flex items-start gap-3 p-3 rounded-[var(--radius-lg)] border border-border-default bg-surface-sunken/40 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 accent-accent-primary"
      />
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
          {icon}
          {label}
        </span>
        {description && (
          <span className="block text-xs text-text-muted mt-0.5">{description}</span>
        )}
      </span>
    </label>
  );
}

/* --------------------------------------------------------------------------
   FieldPreview — mostra come apparirebbe all'operatrice
   -------------------------------------------------------------------------- */

interface FieldPreviewProps {
  label: string;
  fieldType: FieldType;
  isRequired: boolean;
  options?: string[];
}

function FieldPreview({ label, fieldType, isRequired, options }: FieldPreviewProps) {
  return (
    <div className="rounded-[var(--radius-lg)] border border-dashed border-border-default p-4 bg-surface-sunken/30">
      <p className="text-xs text-text-muted uppercase tracking-wide mb-3">
        Anteprima operatrice
      </p>
      <div>
        <p className="text-sm font-medium text-text-primary mb-1.5">
          {label}
          {isRequired && <span className="text-status-negative ml-0.5">*</span>}
        </p>
        <PreviewControl fieldType={fieldType} options={options} />
      </div>
    </div>
  );
}

function PreviewControl({
  fieldType,
  options,
}: {
  fieldType: FieldType;
  options?: string[];
}) {
  switch (fieldType) {
    case 'text':
      return <Input disabled placeholder="Risposta…" />;
    case 'textarea':
      return (
        <textarea
          disabled
          rows={3}
          className="w-full px-3 py-2 rounded-[var(--radius-md)] border border-border-default bg-surface-card text-sm"
          placeholder="Risposta libera…"
        />
      );
    case 'number':
      return <Input disabled type="number" placeholder="0" />;
    case 'date':
      return <Input disabled type="date" />;
    case 'boolean':
      return (
        <div className="flex gap-2">
          <span className="px-3 py-1.5 rounded-[var(--radius-md)] border border-border-default text-sm text-text-muted">
            Sì
          </span>
          <span className="px-3 py-1.5 rounded-[var(--radius-md)] border border-border-default text-sm text-text-muted">
            No
          </span>
        </div>
      );
    case 'select':
    case 'multi_select':
      return options && options.length > 0 ? (
        <div className="flex gap-2 flex-wrap">
          {options.slice(0, 6).map((o) => (
            <span
              key={o}
              className="px-3 py-1.5 rounded-[var(--radius-md)] border border-border-default text-sm text-text-muted"
            >
              {o}
            </span>
          ))}
          {options.length > 6 && (
            <span className="px-3 py-1.5 text-sm text-text-muted">+{options.length - 6}</span>
          )}
        </div>
      ) : (
        <p className="text-xs text-text-muted italic">Nessuna opzione configurata</p>
      );
    case 'photos':
      return (
        <div className="flex h-20 items-center justify-center rounded-[var(--radius-md)] border border-dashed border-border-default text-xs text-text-muted">
          Caricamento foto
        </div>
      );
    case 'body_map':
      return (
        <div className="flex h-20 items-center justify-center rounded-[var(--radius-md)] border border-dashed border-border-default text-xs text-text-muted">
          Mappa corpo
        </div>
      );
    default:
      return null;
  }
}
